import { FC, Fragment } from "react";
import {
  IncreaseOfShareCapitalForm,
  SubscriberForCapitalIncrease,
} from "./increaseOfShareCapitalDomain";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { observer } from "mobx-react";
import { MTextInput } from "@/components-v2/TextInput";

import { useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { IconButton, LinkButton } from "@/components-v2/Button";
import { Icon } from "@/components-v2/Icon";
import { Dialog } from "@/components-v2/Dialog";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { NumberField } from "@/dbot/common/commonComponents";
import { CURRENCY_NOK } from "@/utils/currency";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { confirmAction, thunk } from "@/events/eventsDomain";
import { MSelect } from "@/components-v2/Select";
import { MDateInput } from "@/components-v2/DateInput";
import { Alert } from "@/components-v2/Alert";
import {
  CurrencyExchangeSource,
  KindContributionModel,
  KindContributionOption,
} from "./contributionInKindDomain";

export const ContributionInKind: FC<{ form: IncreaseOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();

  return (
    <>
      <VBox gap={"md"}>
        <div className={"text-lg"}>
          <b>{t("subscriber.contributionInKind")}</b>
        </div>
        <div className={"text-sm"}>{t("subscriber.contributionInKind.info")}</div>
        {form.hasContributionInKind ? (
          <Grid alignItems={"center"}>
            {form.subscribersWithContributionsInKind.map((subscriber) => {
              return (
                <Fragment key={subscriber.subscriberid}>
                  <GridItem xs={6}>
                    <Box alignItems={"center"}>
                      <Icon name={"article"} />
                      {subscriber.nameField.inputValue}
                    </Box>
                  </GridItem>
                  <GridItem xs={2} textAlign={"right"}>
                    {formatNumber(subscriber.kindContributionModel.value.toNumberOrZero())}
                  </GridItem>
                  <GridItem xs={4} textAlign={"right"}>
                    <LinkButton
                      label={{ key: "edit" }}
                      icon={"edit"}
                      onClick={() => form.openContributionInKindDialog(subscriber)}
                      disabled={form.isProjectLocked}
                    />
                    <IconButton
                      icon={"trash"}
                      tooltip={{ key: "delete" }}
                      onClick={() =>
                        form.dispatch(
                          confirmAction({
                            text: {
                              key: "subscriber.contributionInKind.delete.confirm",
                            },
                            onConfirm: [thunk(() => subscriber.setContributionInKind())],
                          }),
                        )
                      }
                      disabled={form.isProjectLocked}
                    />
                  </GridItem>
                </Fragment>
              );
            })}
            <GridItem xs={6}>
              {form.contributionInKindOptions.length > 0 && (
                <DropdownMenu
                  disabled={form.isProjectLocked}
                  button={{ type: "link", label: { key: "subscriber.select" }, icon: "plus" }}
                  items={form.contributionInKindOptions.map((subscriber) => {
                    return {
                      type: "item",
                      label: subscriber.nameField.inputValue,
                      isDisabled: form.isProjectLocked,
                      onSelect: () => form.openContributionInKindDialog(subscriber),
                    };
                  })}
                />
              )}
            </GridItem>
            <GridItem xs={2} textAlign={"right"}>
              {formatNumber(form.totalContributionInKind)}
            </GridItem>
          </Grid>
        ) : (
          <Box>
            <DropdownMenu
              disabled={form.isProjectLocked || form.contributionInKindOptions.length === 0}
              button={{
                type: "link",
                label: { key: "subscriber.select" },
                icon: "plus",
                tooltip:
                  form.contributionInKindOptions.length === 0
                    ? { key: "subscriber.contributionInKind.select.disabled" }
                    : undefined,
              }}
              items={form.contributionInKindOptions.map((subscriber) => {
                return {
                  type: "item",
                  label: subscriber.nameField.inputValue,
                  onSelect: () => form.openContributionInKindDialog(subscriber),
                };
              })}
            />
          </Box>
        )}
      </VBox>
      <hr />
    </>
  );
});

export const AddContributionInKindDialog: FC<{
  selectedSubscriber: SubscriberForCapitalIncrease;
  model: KindContributionModel;
  onClose: () => void;
}> = observer(({ selectedSubscriber, model, onClose }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      maxWidth={"sm"}
      title={selectedSubscriber.nameField.inputValue}
      onCancel={onClose}
      isSubmitDisabled={model.isEmpty}
      onSubmit={() => {
        if (model.validate()) {
          selectedSubscriber.setContributionInKind(
            model.toKindContribution(),
            model.address.validatedValue,
          );
          onClose();
        }
      }}
    >
      <VBox gap={"md"}>
        <Box>
          {t("subscriber.kindContribution.totalSubscription", {
            currency: CURRENCY_NOK,
            totalPrice: {
              type: "number",
              value: model.totalPriceForSubscriber,
            },
          })}
        </Box>
        <Box>{t("subscriber.kindContribution.specifyType")}</Box>
        <MSelect model={model.kindContributionMethod} label={{ key: "type" }} />
        {model.kindContributionMethod.value !== null && (
          <>
            {model.isCurrencyContribution && (
              <>
                {model.kindContributionMethod.value === KindContributionOption.OTHER_CURRENCY && (
                  <MTextInput
                    model={model.currencyContribution.currency}
                    label={{ key: "subscriber.kindContribution.currency" }}
                  />
                )}
                <NumberField
                  field={model.currencyContribution.value}
                  label={t(
                    model.currencyContribution.currency.isEmpty
                      ? "subscriber.kindContribution.amount"
                      : "subscriber.kindContribution.amountInCurrency",
                    {
                      currency: model.currencyContribution.currency.isEmpty
                        ? undefined
                        : model.currencyContribution.currency.inputValue,
                    },
                  )}
                />
                <NumberField
                  field={model.currencyContribution.exchangeRate}
                  label={t("subscriber.kindContribution.exchangeRate")}
                />
                <Box>
                  <MSelect
                    model={model.currencyContribution.source}
                    label={{ key: "subscriber.kindContribution.exchangeSource" }}
                  />
                  <MDateInput
                    model={model.currencyContribution.sourceDate}
                    label={{ key: "date" }}
                  />
                </Box>
                {model.hasCurrencyContributions && (
                  <Alert
                    message={{
                      key: "subscriber.kindContribution.info.multipleCurrencyContributions",
                    }}
                    type={"info"}
                  />
                )}
                {model.currencyContribution.source.value === CurrencyExchangeSource.OTHER && (
                  <MTextInput
                    model={model.currencyContribution.otherSource}
                    label={{ key: "subscriber.kindContribution.otherSource" }}
                  />
                )}
              </>
            )}
            <NumberField
              prefix={CURRENCY_NOK}
              field={model.value}
              label={t("subscriber.kindContribution.value")}
            />
            {model.isOtherKindContribution && (
              <LocalizedTextEditor model={model.description} useV2AutotranslateOption />
            )}
            <MTextInput label={{ key: "address" }} model={model.address} />
          </>
        )}
      </VBox>
    </Dialog>
  );
});
